import { animate } from 'motion/mini';

const emphasizedEase = [0.22, 1, 0.36, 1] as const;
const standardEase = [0.4, 0, 0.2, 1] as const;
type ScrollAxis = 'x' | 'y';
type ScrollFrames = Partial<Record<ScrollAxis, number>>;
const scrollFrames = new WeakMap<HTMLElement, ScrollFrames>();

function reducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function element(root: ParentNode, selector: string) {
  return root.querySelector<HTMLElement>(selector);
}

function scrollPosition(target: HTMLElement, axis: ScrollAxis) {
  return axis === 'x' ? target.scrollLeft : target.scrollTop;
}

function setScrollPosition(target: HTMLElement, axis: ScrollAxis, value: number) {
  if (axis === 'x') target.scrollLeft = value;
  else target.scrollTop = value;
}

function cancelScroll(target: HTMLElement, axis?: ScrollAxis) {
  const frames = scrollFrames.get(target);
  if (!frames) return;
  const axes: ScrollAxis[] = axis ? [axis] : ['x', 'y'];
  axes.forEach((currentAxis) => {
    const frame = frames[currentAxis];
    if (frame) cancelAnimationFrame(frame);
    delete frames[currentAxis];
  });
}

async function exitOverlay(root: ParentNode, overlaySelector: string, panelSelector: string) {
  const overlay = element(root, overlaySelector);
  if (!overlay || reducedMotion()) return;
  const panel = element(overlay, panelSelector);
  const animations = [animate(overlay, { opacity: [1, 0] }, { duration: 0.16, ease: standardEase })];
  if (panel) animations.push(animate(panel, { opacity: [1, 0], transform: ['translateY(0px) scale(1)', 'translateY(5px) scale(.99)'] }, { duration: 0.16, ease: standardEase }));
  await Promise.all(animations);
}

export const motionService = {
  enterScreen(root: ParentNode) {
    if (reducedMotion()) return;
    const page = element(root, '.page');
    if (!page) return;
    animate(page, { opacity: [0, 1], transform: ['translateY(7px)', 'translateY(0px)'] }, { duration: 0.32, ease: emphasizedEase });
  },

  enterOverlay(root: ParentNode, overlaySelector: string, panelSelector: string) {
    if (reducedMotion()) return;
    const overlay = element(root, overlaySelector);
    if (!overlay) return;
    const panel = element(overlay, panelSelector);
    animate(overlay, { opacity: [0, 1] }, { duration: 0.2, ease: standardEase });
    if (panel) animate(panel, { opacity: [0, 1], transform: ['translateY(9px) scale(.985)', 'translateY(0px) scale(1)'] }, { duration: 0.3, ease: emphasizedEase });
  },

  enterStatus(root: ParentNode, initial: boolean) {
    if (reducedMotion()) return;
    const status = element(root, '.live-status');
    if (!status) return;
    const hero = element(status, '.live-status__hero-icon');
    const heading = element(status, '.live-status__stage h1');
    const message = element(status, '.live-status__stage-copy > p');
    const timeline = element(status, '.live-status__timeline');
    const activeIcon = element(status, '.live-status__timeline li.is-active > i');
    if (hero) animate(hero, { opacity: [0.35, 1], transform: ['scale(.94)', 'scale(1)'] }, { duration: 0.42, ease: emphasizedEase });
    if (heading) animate(heading, { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0px)'] }, { duration: 0.38, delay: 0.04, ease: emphasizedEase });
    if (message) animate(message, { opacity: [0, 1], transform: ['translateY(5px)', 'translateY(0px)'] }, { duration: 0.34, delay: 0.09, ease: emphasizedEase });
    if (timeline && initial) animate(timeline, { opacity: [0, 1], transform: ['translateY(6px)', 'translateY(0px)'] }, { duration: 0.4, delay: 0.12, ease: emphasizedEase });
    status.querySelectorAll<HTMLElement>('.live-status__timeline li.is-complete .live-status__connector > span').forEach((connector, index) => {
      animate(connector, { transform: ['scaleX(0)', 'scaleX(1)'] }, { duration: 0.42, delay: 0.12 + index * 0.055, ease: emphasizedEase });
    });
    if (activeIcon) animate(activeIcon, { opacity: [0.6, 1], transform: ['scale(.9)', 'scale(1.035)', 'scale(1)'] }, { duration: 0.4, delay: 0.14, ease: emphasizedEase });
    if (initial) {
      const receipt = element(status, '.live-status__receipt');
      const actions = element(status, '.live-status__actions');
      if (receipt) animate(receipt, { opacity: [0, 1], transform: ['translateY(7px)', 'translateY(0px)'] }, { duration: 0.38, delay: 0.08, ease: emphasizedEase });
      if (actions) animate(actions, { opacity: [0, 1], transform: ['translateY(5px)', 'translateY(0px)'] }, { duration: 0.34, delay: 0.14, ease: emphasizedEase });
    }
  },

  scrollTo(target: HTMLElement, destination: number, axis: ScrollAxis = 'y', animated = true) {
    cancelScroll(target, axis);
    const start = scrollPosition(target, axis);
    const max = axis === 'x' ? target.scrollWidth - target.clientWidth : target.scrollHeight - target.clientHeight;
    const end = Math.max(0, Math.min(destination, max));
    const distance = end - start;
    if (!animated || reducedMotion() || Math.abs(distance) < 2) {
      setScrollPosition(target, axis, end);
      return;
    }
    const duration = Math.min(620, Math.max(300, 260 + Math.abs(distance) * 0.1));
    const startedAt = performance.now();
    const frames = scrollFrames.get(target) ?? {};
    scrollFrames.set(target, frames);
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - ((-2 * progress + 2) ** 3) / 2;
      setScrollPosition(target, axis, start + distance * eased);
      if (progress < 1) frames[axis] = requestAnimationFrame(tick);
      else delete frames[axis];
    };
    frames[axis] = requestAnimationFrame(tick);
  },

  cancelScroll(target: HTMLElement) {
    cancelScroll(target);
  },

  exitProduct(root: ParentNode) {
    return exitOverlay(root, '.product-overlay', '.product-modal');
  },

  exitService(root: ParentNode) {
    return exitOverlay(root, '.service-overlay', '.service-sheet');
  },
};
